import { mockProductsData } from '../mock/productsData';

let cachedPrices = null;

/**
 * Fetch dynamic prices from backend flat-file JSON endpoint.
 * Falls back to mockProductsData if the endpoint fails or is unreachable.
 */
export async function fetchDynamicPrices() {
  try {
    const res = await fetch(`/api/get_prices.php?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const result = await res.json();
    if (result.status === 'success' && result.data && Array.isArray(result.data.products)) {
      cachedPrices = result.data.products;
      return result.data.products;
    }
  } catch (err) {
    console.warn('[pricingService] Could not fetch dynamic prices, using fallback mock data:', err.message);
  }
  return cachedPrices || null;
}

/**
 * Merges dynamic prices into the full mockProductsData catalog.
 * @param {Array} dynamicPricesList - Array of { id, price_per_sqft, offer_percentage }
 * @returns {Object} Complete products structure with updated prices
 */
export function getMergedProductsData(dynamicPricesList) {
  const pricesList = dynamicPricesList || cachedPrices;
  if (!pricesList || !Array.isArray(pricesList) || pricesList.length === 0) {
    return mockProductsData;
  }

  const priceMap = new Map();
  pricesList.forEach((item) => {
    if (item && item.id) {
      priceMap.set(String(item.id), item);
    }
  });

  const mergedProducts = mockProductsData.products.map((product) => {
    const priceInfo = priceMap.get(String(product._id));
    if (!priceInfo) return product;

    const newPrice = Number(priceInfo.price_per_sqft);
    const newOffer = Number(priceInfo.offer_percentage);

    return {
      ...product,
      price_per_sqft: !Number.isNaN(newPrice) ? newPrice : product.price_per_sqft,
      offer_percentage: !Number.isNaN(newOffer) ? newOffer : product.offer_percentage,
      standard_items: Array.isArray(product.standard_items)
        ? product.standard_items.map((std) => ({
            ...std,
            offer_percentage: !Number.isNaN(newOffer) ? newOffer : std.offer_percentage,
          }))
        : product.standard_items,
    };
  });

  return {
    ...mockProductsData,
    products: mergedProducts,
  };
}

/**
 * Save updated prices to backend flat-file JSON storage (Admin only).
 * @param {string} token - Admin JWT token
 * @param {Array} products - Array of { id, name, category, price_per_sqft, offer_percentage }
 */
export async function saveDynamicPrices(token, products) {
  const res = await fetch('/api/update_prices.php', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ products }),
  });

  const data = await res.json();
  if (res.ok && data.status === 'success') {
    if (data.data && Array.isArray(data.data.products)) {
      cachedPrices = data.data.products;
    }
    return { success: true, message: data.message, data: data.data };
  }

  throw new Error(data.message || 'Failed to update prices');
}
